import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Camera,
  ExternalLink,
  FileText,
  Info,
  Images,
  NotebookPen,
  Trash2,
} from 'lucide-react'
import { usePoints, useUpdatePoint } from './usePoints'
import {
  useAddDocument,
  useAddMedia,
  useDeleteDocument,
  useDeleteMedia,
  useDocuments,
  useMedia,
} from '../live/useToday'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import { POINT_STATUS_META, POINT_TYPE_META } from '../../shared/lib/pointMeta'
import { gmapsPlaceLink } from '../../shared/lib/gmapsLink'
import { cn } from '../../shared/lib/cn'
import type { PointStatus, TripPoint } from '../../shared/api/types'

type Tab = 'info' | 'media' | 'docs' | 'notes'

const TABS: { id: Tab; Icon: typeof Info }[] = [
  { id: 'info', Icon: Info },
  { id: 'media', Icon: Images },
  { id: 'docs', Icon: FileText },
  { id: 'notes', Icon: NotebookPen },
]

const STATUS_OPTIONS: PointStatus[] = ['planned', 'visited', 'changed', 'skipped']

export function PointDetail() {
  const { t } = useTranslation()
  const { id, pointId } = useParams<{ id: string; pointId: string }>()
  const tripId = id as string
  const points = usePoints(id)
  const [tab, setTab] = useState<Tab>('info')

  const point = useMemo(
    () => points.data?.find((p) => p.id === pointId) ?? null,
    [points.data, pointId],
  )

  if (points.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    )
  }

  if (!point) {
    return (
      <EmptyState
        title={t('point_detail.not_found')}
        description={t('point_detail.removed')}
        action={
          <Link to={`/trips/${tripId}/timeline`} className="text-sm text-st-primary hover:underline">
            {t('point_detail.back')}
          </Link>
        }
      />
    )
  }

  const { Icon } = POINT_TYPE_META[point.type]
  const status = POINT_STATUS_META[point.status]

  return (
    <div className="mx-auto max-w-2xl pb-12">
      <Link
        to={`/trips/${tripId}/timeline`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> {t('point_detail.back')}
      </Link>

      <div className="mb-5 flex items-start gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-st-md bg-st-surface-2 text-st-primary">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold text-st-text">{point.title}</h1>
            <span className="text-sm" style={{ color: status.color }} title={t(`point_detail.status_${point.status}`)}>
              {status.symbol}
            </span>
          </div>
          <p className="text-sm text-st-muted">
            {t(`point_types.${point.type}`)}
            {point.address ? ` · ${point.address}` : ''}
          </p>
        </div>
      </div>

      <div className="mb-5 flex gap-1 border-b border-st-line">
        {TABS.map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            onClick={() => setTab(tabItem.id)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition',
              tab === tabItem.id
                ? 'border-b-2 border-st-primary text-st-primary'
                : 'text-st-muted hover:text-st-text',
            )}
          >
            <tabItem.Icon className="h-4 w-4" /> {t(`point_detail.tab_${tabItem.id}`)}
          </button>
        ))}
      </div>

      {tab === 'info' && <InfoTab point={point} tripId={tripId} />}
      {tab === 'media' && <MediaTab tripId={tripId} pointId={point.id} />}
      {tab === 'docs' && <DocsTab tripId={tripId} pointId={point.id} />}
      {tab === 'notes' && <NotesTab tripId={tripId} point={point} />}
    </div>
  )
}

function InfoTab({ point, tripId }: { point: TripPoint; tripId: string }) {
  const { t } = useTranslation()
  const update = useUpdatePoint(tripId)
  return (
    <div className="space-y-4">
      <div>
        <p className="mb-1 text-xs font-medium text-st-muted">{t('point_detail.status')}</p>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => update.mutate({ pointId: point.id, patch: { status: s } })}
              disabled={update.isPending}
              className={cn(
                'rounded-st-full px-3 py-1 text-xs font-medium capitalize transition',
                point.status === s
                  ? 'bg-st-primary text-white'
                  : 'border border-st-line bg-st-surface text-st-muted hover:text-st-text',
              )}
            >
              {t(`point_detail.status_${s}`)}
            </button>
          ))}
        </div>
      </div>

      {point.description && (
        <div>
          <p className="mb-1 text-xs font-medium text-st-muted">{t('point_detail.description')}</p>
          <p className="text-sm text-st-text">{point.description}</p>
        </div>
      )}

      {(point.lat != null || point.address) && (
        <a
          href={gmapsPlaceLink(point.lat, point.lng, `${point.title} ${point.address}`)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-st-primary hover:underline"
        >
          <ExternalLink className="h-4 w-4" /> {t('point_detail.open_maps')}
        </a>
      )}
    </div>
  )
}

function MediaTab({ tripId, pointId }: { tripId: string; pointId: string }) {
  const { t } = useTranslation()
  const media = useMedia(tripId, pointId)
  const add = useAddMedia(tripId)
  const remove = useDeleteMedia(tripId)
  const fileRef = useRef<HTMLInputElement>(null)

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) add.mutate({ file, point_id: pointId })
    e.target.value = ''
  }

  return (
    <div>
      <div className="mb-4">
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={add.isPending}>
          <Camera className="h-4 w-4" /> {add.isPending ? t('point_detail.uploading') : t('point_detail.add_photo')}
        </Button>
        <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={onChange} />
      </div>

      {media.isLoading ? (
        <Spinner />
      ) : media.data && media.data.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {media.data.map((m) => (
            <div key={m.id} className="group relative aspect-square overflow-hidden rounded-st-md bg-st-surface-2">
              <img src={m.url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => remove.mutate(m.id)}
                className="absolute right-1 top-1 rounded-st-sm bg-black/50 p-1 text-white opacity-0 transition group-hover:opacity-100"
                aria-label={t('point_detail.delete_photo')}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-st-muted">{t('point_detail.no_photos')}</p>
      )}
    </div>
  )
}

function DocsTab({ tripId, pointId }: { tripId: string; pointId: string }) {
  const { t } = useTranslation()
  const docs = useDocuments(tripId, pointId)
  const add = useAddDocument(tripId)
  const remove = useDeleteDocument(tripId)
  const fileRef = useRef<HTMLInputElement>(null)

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) add.mutate({ file, point_id: pointId })
    e.target.value = ''
  }

  return (
    <div>
      <div className="mb-4">
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={add.isPending}>
          <FileText className="h-4 w-4" /> {add.isPending ? t('point_detail.uploading') : t('point_detail.add_document')}
        </Button>
        <input ref={fileRef} type="file" hidden onChange={onChange} />
      </div>

      {docs.isLoading ? (
        <Spinner />
      ) : docs.data && docs.data.length > 0 ? (
        <ul className="space-y-2">
          {docs.data.map((d) => (
            <li key={d.id} className="flex items-center gap-3 rounded-st-md border border-st-line bg-st-surface p-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-st-md bg-st-surface-2 text-st-primary">
                <FileText className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-st-text capitalize">{d.type}</p>
                <p className="truncate text-xs text-st-muted">{d.file_url.split('/').pop()}</p>
              </div>
              <button
                type="button"
                onClick={() => remove.mutate(d.id)}
                className="rounded-st-sm p-1.5 text-st-muted hover:bg-st-danger/10 hover:text-st-danger"
                aria-label={t('point_detail.delete_document')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-st-muted">{t('point_detail.no_documents')}</p>
      )}
    </div>
  )
}

function NotesTab({ tripId, point }: { tripId: string; point: TripPoint }) {
  const { t } = useTranslation()
  const update = useUpdatePoint(tripId)
  const [draft, setDraft] = useState(point.notes)

  useEffect(() => {
    setDraft(point.notes)
  }, [point.notes])

  const dirty = draft !== point.notes
  return (
    <div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={8}
        placeholder={t('point_detail.notes_hint')}
        className="w-full rounded-st-md border border-st-line bg-st-surface p-3 text-sm text-st-text focus:border-st-primary focus:outline-none"
      />
      <div className="mt-3 flex justify-end">
        <Button
          onClick={() => update.mutate({ pointId: point.id, patch: { notes: draft } })}
          disabled={!dirty || update.isPending}
        >
          {update.isPending ? t('point_detail.saving') : t('point_detail.save_notes')}
        </Button>
      </div>
    </div>
  )
}
